import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Flip, toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import MainButton from "../../../components/common/MainButton/MainButton";
import TextFiled from "../../../components/common/InputField/TextFiled";
import Heading from "../../../components/common/Typography/Heading";
import Paragraph from "../../../components/common/Typography/Paragraph";
import InteractiveText from "../../../components/common/Typography/InteractiveText";
import AuxiliaryText from "../../../components/common/Typography/AuxiliaryText";
import WrittenStepper from "../../../components/common/StepIndicater/WrittenStepper";
import SpecialText from "../../../components/common/Typography/SpecialText";
import Spinner from "../../../components/common/Spinner";
import StepBackButton from "../../../components/common/StepBackButton/StepBackButton";

import { verificationCodeSchema } from "../../../FormValidations/SignUpSchema";
import { setField, resetForm, setStep } from "../../../Redux/Slice/SignUp";
import { SendOtpMutation, VerifyOtpMutation, RegisterUserMutation } from "../../../services/SignUp";
import { useGraphQL } from "../../../context/GraphQLProvider";
import { POPUP_FORM_CONTENT_CLASS, POPUP_FORM_CONTENT_MAX_WIDTH_CLASS } from "../../../components/popup/popupLayoutSpec";

const Verify = ({ label, closePopup }) => {
  const graphqlService = useGraphQL();
  const dispatch = useDispatch();
  const [RequestStart, setRequetStart] = useState(false);
  const [currentStep, setCurrentStep] = useState("A");
  const InitialValues = useSelector((state) => state.signup);
  const { id, email, password, step } = useSelector((state) => state.signup);
  
  // State for 6-digit verification code
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const [showResend, setShowResend] = useState(false);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
    } else {
      setShowResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Format timer to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle input change for individual digits
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Update formik value
    const otpString = newOtp.join('');
    formik.setFieldValue('verificationCode', otpString);
    
    // Auto-focus next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste functionality
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      formik.setFieldValue('verificationCode', pastedData);
      
      // Focus the last input
      inputRefs.current[5]?.focus();
    }
  };

  const ResendOtpUser = (gql, email) => {
    setRequetStart(true);
    SendOtpMutation(gql, {
      email,
    })
    .then((response) => {
        if (response.sendOtp === 'OTP sent successfully'){
          setRequetStart(false);
          setTimer(120); // Reset timer to 2 minutes
          setShowResend(false); // Hide resend button
          toast.success("Verification Code Sent!", {
            position: toast.POSITION.BOTTOM_RIGHT,
            transition: Flip,
            className: "form-validation"
          });
        }
    })
    .catch((error) => {
      console.log("error", error);
    });
  };

  const RegisterUser = (gql, userInput) => {
    RegisterUserMutation(gql, userInput)
    .then((response) => {
      console.log(response);
        if (response.registerUser?.id){
          const userId = response.registerUser?.id
          dispatch(setField({id: userId}))
          setCurrentStep("B");
          setTimeout(closePopup, 3000);
          setRequetStart(false);
        }
    })
    .catch((error) => {
      console.log("error", error);
    })
    .finally(() => {
      // setStep("B");
      // setTimeout(closePopup, 3000);
      setRequetStart(false);
    });
  };

  const formik = useFormik(
      {
        initialValues: InitialValues,
        validationSchema: verificationCodeSchema,
        onSubmit: (values, { setErrors, setSubmitting }) => {
          setRequetStart(true);
          const currentDateTime = new Date().toISOString();
          dispatch(setField({ ...values, startDate: currentDateTime }));
          const otp = values.verificationCode;

          VerifyOtpMutation(graphqlService, { email, otp })
            .then((response) => {
              console.log(response);
              if (response.verifyOtp.message === 'OTP verified successfully') {
                const userInput = {
                  "userInput": {
                    "personalEmail": email,
                    "password": password,
                    "startDate": currentDateTime
                  }
                };
                RegisterUser(graphqlService, userInput);
              } else if (response.verifyOtp.message === 'Error verifying OTP') {
                setErrors({ verificationCode: "Invalid OTP" });
                setRequetStart(false);
              }
            })
            .catch((error) => {
              console.log("error", error);
              setRequetStart(false);
            })
            .finally(() => {
              // setStep("B");
              // setTimeout(closePopup, 3000);
              // setRequetStart(false);
            });
        },
      }
    );

  const handleStepChange = (newStep) => {
    dispatch(setStep(newStep));
  };

  return (
    <>
      {RequestStart && <Spinner />}
      <ToastContainer autoClose={2000} />
      {currentStep === "A" && (
        <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <Heading
              variant={"h6"}
              className={"text-center text-offblack"}
            >
              Verify your email
            </Heading>
            <div className="mt-10 xl:mt-[30px] w-full">
              <div className={`${POPUP_FORM_CONTENT_MAX_WIDTH_CLASS} mx-auto`}>
                <Paragraph
                  variant={"BodyCompact"}
                  className={"text-offblack text-start"}
                >
                  We've sent a verification link to your email. Please click the
                  link or enter the 6-digit code to verify your account.
                </Paragraph>
              </div>
              <div className="mt-5 w-full">
                  <div className="flex justify-between" onPaste={handlePaste}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-12 h-12 text-center text-lg border border-solid border-[#C0C0C0] border-[1.4px] rounded focus:border-primaryblue"
                        placeholder=""
                      />
                    ))}
                  </div>
                  {formik.touched.verificationCode && formik.errors.verificationCode && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.verificationCode}
                    </div>
                  )}
                <div className="flex items-center justify-between mt-3">
                  {showResend ? (
                    <>
                      <AuxiliaryText variant={"Infographic"} className={"mr-2 text-mediumgray"}>
                        Didn't get the code?
                      </AuxiliaryText>
                      <InteractiveText
                        variant={"ActionLink"}
                        className={"text-primaryblue cursor-pointer"}
                        onClick={() => {
                          ResendOtpUser(graphqlService,email)
                        }}
                      >
                        Resend
                      </InteractiveText>
                    </>
                  ) : (
                    <div className="w-full text-start">
                      <SpecialText variant={"Counter"} className={"text-[#C0C0C0]"}>
                        {formatTime(timer)}
                      </SpecialText>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <div className="flex items-center justify-between mt-10 w-full">
                    <StepBackButton onClick={() => handleStepChange(step - 1)} />
                    <MainButton
                      variant={"small"}
                      onClick={formik.handleSubmit}
                    >
                      Next
                    </MainButton>
                  </div>
                </div>
              </div>
            </div>
            {/* <WrittenStepper
              currentStep={2}
              lastStep={8}
              currentLabel={"Verify your email"}
              marginTop={"absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full"}
            /> */}
          </div>
        </div>
      )}

      {currentStep === "B" && (
        <>
          <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
            <div className={POPUP_FORM_CONTENT_CLASS}>
              <Heading
                variant={"h6"}
                className={
                  "flex items-center justify-center text-center text-offblack"
                }
              >
                Email verified !!!
              </Heading>
              <div className="mt-10 xl:mt-8 w-full">
                <div className="flex justify-center w-full">
                  <div className="w-full mx-auto">
                    <Paragraph
                      variant={"BodyCompact"}
                      className={"text-offblack text-center"}
                    >
                      Your business email has been successfully verified. You're now
                      ready to receive updates and notifications directly to your
                      inbox.
                    </Paragraph>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Verify;
